import { MenuOption } from '@mgk-eld/utils';
import {
  Checkbox,
  FormControl,
  FormHelperText,
  InputLabel,
  ListItemText,
  MenuItem,
  Select as MuiSelect,
  SelectChangeEvent,
  SelectProps,
} from '@mui/material';
import React, { useCallback, useMemo } from 'react';

const ReportsMultiSelect: React.FC<
  {
    items: MenuOption[];
    showErrorMessage: boolean;
    errorMessage: string;
    setValue: (val: string) => void;
    value: string;
    withCheckbox?: boolean;
    allTitle?: string;
  } & Omit<SelectProps<string[]>, 'value' | 'onChange' | 'renderValue'>
> = ({
  items,
  showErrorMessage,
  errorMessage,
  setValue,
  id,
  error,
  title,
  value,
  disabled,
  withCheckbox = false,
  allTitle = 'All',
  ...rest
}) => {
  const ALL_VALUE = 'all';

  const allItemValues = useMemo(
    () => items.map((item) => String(item.value)),
    [items]
  );

  const selectedValues = useMemo(
    () => value.split(',').filter((v): v is string => !!v),
    [value]
  );

  const handleChange = useCallback(
    (e: SelectChangeEvent<string[]>) => {
      const val = e.target.value as string[];

      const lastSelected = val[val.length - 1];
      const isAllToggled = lastSelected === ALL_VALUE;
      const isCurrentlyAllSelected =
        selectedValues.length === allItemValues.length;

      let newValues: string[];

      if (isAllToggled) {
        newValues = isCurrentlyAllSelected ? [] : allItemValues;
      } else {
        newValues = val.filter((v) => v !== ALL_VALUE);
      }

      setValue(newValues.join(','));
    },
    [allItemValues, selectedValues, setValue]
  );

  const renderSelectValue = useCallback(
    (selected: string[]) => {
      if (selected.length === allItemValues.length) {
        return allTitle;
      }
      const titles = items
        .filter((item) => selected.includes(String(item.value)))
        .map((item) => item.title);
      return titles.join(', ');
    },
    [items, allItemValues, allTitle]
  );

  const displayOptions = useMemo(
    () =>
      withCheckbox ? [{ title: allTitle, value: ALL_VALUE }, ...items] : items,
    [items, withCheckbox, allTitle]
  );

  const getIsSelected = (itemValue: string) => {
    return itemValue === ALL_VALUE
      ? selectedValues.length === allItemValues.length
      : selectedValues.includes(itemValue) ||
          selectedValues.length === allItemValues.length;
  };

  return (
    <FormControl id={id} variant="filled" fullWidth error={error}>
      <InputLabel id={id}>{title}</InputLabel>
      <MuiSelect<string[]>
        multiple
        label="Status"
        value={selectedValues}
        onChange={handleChange}
        renderValue={renderSelectValue}
        disabled={disabled}
        autoFocus={false}
        MenuProps={{
          autoFocus: false,
          disableAutoFocusItem: true,
        }}
        {...rest}
      >
        {displayOptions.map((item) => {
          const itemValue = String(item.value);
          const isSelected = getIsSelected(itemValue);
          return (
            <MenuItem
              key={itemValue}
              value={itemValue}
              disabled={item.disabled}
              sx={{ py: withCheckbox ? '1px !important' : 'inherit' }}
            >
              {withCheckbox ? (
                <>
                  <Checkbox size="small" checked={isSelected} />
                  <ListItemText primary={item.title} />
                </>
              ) : (
                item.title
              )}
            </MenuItem>
          );
        })}
      </MuiSelect>
      {showErrorMessage && <FormHelperText>{errorMessage}</FormHelperText>}
    </FormControl>
  );
};

export default ReportsMultiSelect;
